import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";

/**
 * 合并类名
 * @param inputs 类名
 * @returns string
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * 防抖函数
 * @param func 函数
 * @param wait 等待时间
 * @returns (...args: Parameters<T>) => void
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * 节流函数
 * @param func 函数
 * @param limit 限制
 * @returns (...args: Parameters<T>) => void
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * 格式化消息日期
 * @param date 日期
 * @returns string
 */
export const messageDateFormatter = (date: string | Date) => {
  // if date is string, convert to Date
  // if date is today, return hh:mm pm/am
  // if date is yesterday, return "Yesterday hh:mm pm/am"
  // if date is within this week, return "Weekday hh:mm pm/am"
  // else return "YYYY-MM-DD hh:mm pm/am"
  const d = new Date(date);
  const dDate = d.getDate();
  const dMonth = d.getMonth();
  const dYear = d.getFullYear();
  const now = new Date();
  const nowDay = now.getDay();
  const nowDate = now.getDate();
  const nowMonth = now.getMonth();
  const nowYear = now.getFullYear();
  const timeString = d.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
  });

  const isToday = dDate === nowDate && dMonth === nowMonth && dYear === nowYear;

  if (isToday) {
    return timeString;
  }

  const isYesterday =
    dDate === nowDate - 1 && dMonth === nowMonth && dYear === nowYear;

  if (isYesterday) {
    return `Yesterday ${timeString}`;
  }

  const isThisWeek = d >= new Date(nowYear, nowMonth, nowDate - nowDay);

  if (isThisWeek) {
    return `${d.toLocaleDateString("en-US", {
      weekday: "long",
    })} ${timeString}`;
  }

  // else return "YYYY-MM-DD hh:mm pm/am"
  return `${dYear}-${(dMonth + 1).toString().padStart(2, "0")}-${dDate
    .toString()
    .padStart(2, "0")} ${timeString}`;
};

/**
 * 格式化日期时间
 * @param date 日期
 * @param format 格式
 * @returns string
 */
export const formatDateTime = (date: string | Date, format: string) => {
  const d = new Date(date);
  // 需要支持 DD MMM YYYY
  const map: { [key: string]: string } = {
    DD: d.getDate().toString().padStart(2, "0"),
    MMM: d.toLocaleString("en-US", { month: "short" }),
    MM: (d.getMonth() + 1).toString().padStart(2, "0"),
    YYYY: d.getFullYear().toString(),
    hh: d.getHours().toString().padStart(2, "0"),
    mm: d.getMinutes().toString().padStart(2, "0"),
    ss: d.getSeconds().toString().padStart(2, "0"),
  };

  return format.replace(/DD|MMM|MM|YYYY|hh|mm|ss/g, (matched) => map[matched]);
};

/**
 * 格式化日期
 * @param date 日期
 * @returns string
 */
export function formatDate(date: string | Date): string {
  const d = new Date(date);
  return d.toLocaleDateString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

/**
 * 格式化时间
 * @param date 日期
 * @returns string
 */
export function formatTime(date: string | Date): string {
  const d = new Date(date);
  return d.toLocaleTimeString("zh-CN", {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * 生成唯一ID
 * @returns string
 */
export function generateId(): string {
  return Math.random().toString(36).substr(2, 9);
}

/**
 * 验证文件类型
 * @param file 文件对象
 * @param allowedTypes 允许的文件类型
 * @returns boolean
 */
export function validateFileType(file: File, allowedTypes: string[]): boolean {
  return allowedTypes.some((type) => {
    if (type.startsWith(".")) {
      return file.name.toLowerCase().endsWith(type.toLowerCase());
    }
    return file.type.toLowerCase().includes(type.toLowerCase());
  });
}

/**
 * 验证文件大小
 * @param file 文件对象
 * @param maxSizeMB 最大大小（MB）
 * @returns boolean
 */
export function validateFileSize(file: File, maxSizeMB: number): boolean {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
}

/**
 * 格式化文件大小
 * @param bytes 字节
 * @returns string
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * 检查是否为移动设备
 * @returns boolean
 */
export function isMobile(): boolean {
  return window.innerWidth < 768;
}

/**
 * 检查是否为平板设备
 * @returns boolean
 */
export function isTablet(): boolean {
  return window.innerWidth >= 768 && window.innerWidth < 1024;
}

/**
 * 检查是否为桌面设备
 * @returns boolean
 */
export function isDesktop(): boolean {
  return window.innerWidth >= 1024;
}
